import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Mail, Calendar, MapPin, Clock, ChevronLeft, CheckCircle2, AlertTriangle, Users, MessageSquare, Phone, Download, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const RESPONSE_PILLS = {
  yes: { label: "Yes", className: "bg-sage-500 text-white" },
  maybe: { label: "Maybe", className: "bg-amber-100 text-amber-800" },
  no: { label: "No", className: "bg-ink-100 text-ink-700" },
};

export default function DepDetail() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(null); // {id, name}

  const load = useCallback(() => {
    return api.get(`/deps/broadcasts/${id}`)
      .then(({ data }) => setB(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load this broadcast"));
  }, [id]);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  const doConfirm = async () => {
    if (!pendingConfirm) return;
    const rid = pendingConfirm.id;
    setConfirming(rid);
    setPendingConfirm(null);
    try {
      const { data } = await api.post(`/deps/broadcasts/${id}/responses/${rid}/confirm`);
      toast.success(`Dep filled${data.courtesy_emails_sent ? ` · ${data.courtesy_emails_sent} courtesy email${data.courtesy_emails_sent === 1 ? "" : "s"} sent` : ""}`);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "already_filled") {
        toast.error("This dep has already been confirmed.");
        await load();
      } else {
        toast.error(formatApiErrorDetail(detail) || e.message);
      }
    } finally { setConfirming(null); }
  };

  const downloadIcs = async () => {
    try {
      const res = await api.get(`/deps/broadcasts/${id}/confirmed.ics`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `amds-dep-${id}.ics`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return <div className="card-base p-6 text-ink-600">Loading…</div>;
  if (!b) return (
    <div className="card-base p-6" data-testid="dep-not-found">
      Broadcast not found. <Link to="/deps" className="text-sage-600 underline">Back to deps</Link>.
    </div>
  );

  const responses = b.responses || [];
  const yeses = responses.filter((r) => r.response === "yes");
  const maybes = responses.filter((r) => r.response === "maybe");
  const nos = responses.filter((r) => r.response === "no");
  const filled = b.status === "filled";

  return (
    <div className="space-y-5" data-testid="dep-detail-page">
      <Link to="/deps" className="inline-flex items-center gap-1 text-sage-600 text-sm" data-testid="back-to-deps">
        <ChevronLeft size={16} /> Back to deps
      </Link>

      <div>
        <div className="label-overline flex items-center gap-2">
          {filled
            ? <span className="pill bg-sage-100 text-sage-700 text-xs"><CheckCircle2 size={12} /> Filled</span>
            : <span className="pill bg-amber-100 text-amber-700 text-xs">Open · awaiting responses</span>}
          <span>{b.is_creator ? "Your broadcast" : "Dep request"}</span>
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">
          {b.instrument} · {b.event_type.replace("_", " ")}
        </h1>
        <p className="text-ink-600 mt-1 text-sm">
          {b.is_creator
            ? <>You broadcast this dep to {(b.delivered || []).length} {(b.delivered || []).length === 1 ? "person" : "people"}.</>
            : <>{b.from_name} sent this to you and others.</>}
        </p>
      </div>

      <section className="card-base p-4 sm:p-5 space-y-2">
        <Row icon={Calendar} label="Date" value={b.date} />
        {b.call_time && <Row icon={Clock} label="Call time" value={b.call_time} />}
        {b.venue && <Row icon={MapPin} label="Venue" value={b.venue} />}
        {b.notes && (
          <div>
            <div className="label-overline mt-1 mb-1">Notes</div>
            <p className="text-ink-700 text-sm whitespace-pre-wrap">{b.notes}</p>
          </div>
        )}
      </section>

      {!b.is_creator && (
        <div className="card-base p-4 sm:p-5 border-l-4 border-sage-400 bg-sage-50/60" data-testid="reply-block">
          <div className="flex items-start gap-3">
            <Mail size={18} className="text-sage-600 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="font-heading font-semibold text-ink-900">Reply directly to {b.from_name}</div>
              <p className="text-sm text-ink-700 mt-1 mb-2">
                AMDS-user replies aren't tracked in-app yet. Just email them back — they'll know it's about this dep.
              </p>
              <a href={`mailto:${b.from_email}?subject=${encodeURIComponent(`Re: ${b.instrument} dep on ${b.date}`)}`}
                 className="btn-primary text-sm" data-testid="reply-email-btn">
                <Mail size={14} /> Reply to {b.from_email}
              </a>
            </div>
          </div>
        </div>
      )}

      {b.is_creator && filled && (
        <section className="card-base p-4 sm:p-5 border-l-4 border-sage-400 bg-sage-50/60" data-testid="filled-block">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={18} className="text-sage-600 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-heading font-semibold text-ink-900">Dep confirmed</div>
              <p className="text-sm text-ink-700 mt-1 mb-2">
                The chosen player has been notified by email. Other respondents have received a courtesy "covered, thanks" note.
              </p>
              <button onClick={downloadIcs} className="btn-secondary text-sm" data-testid="ics-download-btn">
                <Download size={14} /> Add to calendar (.ics)
              </button>
            </div>
          </div>
        </section>
      )}

      {b.is_creator && (
        <section className="card-base p-4 sm:p-5" data-testid="responses-block">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <MessageSquare size={16} className="text-sage-600" />
              <h2 className="font-heading text-lg font-semibold">Responses</h2>
            </div>
            <div className="flex gap-1.5 text-xs">
              <span className="pill bg-sage-100 text-sage-700"><CheckCircle2 size={11} /> {yeses.length} yes</span>
              <span className="pill bg-amber-100 text-amber-700">{maybes.length} maybe</span>
              <span className="pill bg-ink-100 text-ink-700">{nos.length} no</span>
            </div>
          </div>

          {responses.length === 0 ? (
            <p className="text-sm text-ink-600">No responses yet. They'll appear here as recipients reply via the email buttons.</p>
          ) : (
            <div className="space-y-2">
              {[...yeses, ...maybes, ...nos].map((r) => (
                <ResponseRow key={r.id} r={r} canConfirm={b.is_creator && !filled && r.response === "yes"}
                  confirming={confirming === r.id}
                  onConfirm={() => setPendingConfirm({ id: r.id, name: r.submitted_name })}
                  filled={filled} />
              ))}
            </div>
          )}
        </section>
      )}

      {b.is_creator && (
        <section className="card-base p-4 sm:p-5" data-testid="delivery-block">
          <div className="flex items-center gap-2 mb-3">
            <Users size={16} className="text-sage-600" />
            <h2 className="font-heading text-lg font-semibold">Delivery</h2>
            <span className="text-xs text-ink-500">({(b.delivered || []).length} total)</span>
          </div>
          <div className="rounded-xl border border-ivory-200 divide-y">
            {(b.delivered || []).map((d, idx) => (
              <div key={idx} className="px-3 py-2 flex items-center justify-between gap-3 text-sm" data-testid={`delivered-row-${idx}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{d.name || d.email}</div>
                  <div className="text-xs text-ink-500 truncate">{d.email} · {d.kind.replace("_", " ")}</div>
                </div>
                {d.sent ? (
                  <span className="pill bg-sage-100 text-sage-700"><CheckCircle2 size={12} /> Sent</span>
                ) : (
                  <span className="pill bg-terracotta-50 text-terracotta-700" title={d.error || ""}>
                    <AlertTriangle size={12} /> Failed
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <AlertDialog open={!!pendingConfirm} onOpenChange={(open) => !open && setPendingConfirm(null)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm {pendingConfirm?.name || "this player"} as the dep?</AlertDialogTitle>
            <AlertDialogDescription>
              The chosen player will receive a confirmation email and you'll get a calendar invite (.ics).
              Other respondents will get a polite "covered, thanks" courtesy email.
              You can't unfill a confirmed dep without contacting them directly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-dialog-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doConfirm} data-testid="confirm-dialog-confirm" className="bg-sage-500 hover:bg-sage-600">
              Confirm dep
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ResponseRow({ r, canConfirm, confirming, onConfirm, filled }) {
  const pill = RESPONSE_PILLS[r.response] || RESPONSE_PILLS.maybe;
  const isConfirmedOne = r.confirmed;
  return (
    <div className={`rounded-xl border p-3 ${isConfirmedOne ? "border-sage-400 bg-sage-50/60" : "border-ivory-200"}`}
         data-testid={`response-row-${r.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`pill ${pill.className}`} data-testid={`response-pill-${r.id}`}>{pill.label}</span>
            <span className="font-medium text-ink-900">{r.submitted_name}</span>
            {isConfirmedOne && <span className="pill bg-sage-600 text-white text-[10px]"><CheckCircle2 size={10} /> Confirmed</span>}
          </div>
          <div className="text-xs text-ink-500 mt-1 flex items-center gap-3 flex-wrap">
            <span><Mail size={11} className="inline mr-1" />{r.submitted_email}</span>
            {r.submitted_phone && <span><Phone size={11} className="inline mr-1" />{r.submitted_phone}</span>}
            {r.recipient_email && r.recipient_email.toLowerCase() !== (r.submitted_email || "").toLowerCase() && (
              <span>· originally emailed to {r.recipient_email}</span>
            )}
          </div>
          {r.message && <p className="text-sm text-ink-700 mt-2 whitespace-pre-wrap">{r.message}</p>}
        </div>
        {canConfirm && (
          <button onClick={onConfirm} disabled={confirming || filled}
            data-testid={`confirm-response-${r.id}`}
            className="btn-primary text-sm whitespace-nowrap">
            <Sparkles size={14} /> {confirming ? "Confirming…" : "Confirm dep"}
          </button>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={16} className="text-sage-500 mt-0.5 shrink-0" />
      <div className="text-sm">
        <span className="text-ink-500">{label}: </span>
        <span className="text-ink-900">{value}</span>
      </div>
    </div>
  );
}
